import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import translations from './translations';

export const SUPPORTED_LOCALES = ['ko', 'en', 'ja', 'zh'];
export const DEFAULT_LOCALE = 'ko';
const STORAGE_KEY = 'qr.locale';

/**
 * Simple, dependency-free i18n system.
 * - LocaleProvider wraps the app and exposes the current locale via context.
 * - useLocale() returns { locale, setLocale, t } for components.
 * - t(key, locale) is also available as a standalone function when there's no context.
 */

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
});

/** Resolve initial locale from storage, navigator, or default. */
function detectInitialLocale() {
  try {
    const stored = typeof window !== 'undefined' && window.localStorage?.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch {}
  if (typeof navigator !== 'undefined' && navigator.language) {
    const short = navigator.language.slice(0, 2).toLowerCase();
    if (SUPPORTED_LOCALES.includes(short)) return short;
  }
  return DEFAULT_LOCALE;
}

/** Replace {placeholders} with provided params. */
function interpolate(str, params) {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
  );
}

/**
 * Standalone translator. Falls back to Korean, then returns the key as-is.
 * @param {string} key
 * @param {string} [locale]
 * @param {object} [params]
 */
export function t(key, locale = DEFAULT_LOCALE, params) {
  const dict = translations[locale] || translations[DEFAULT_LOCALE];
  const raw = dict?.[key] ?? translations[DEFAULT_LOCALE]?.[key] ?? key;
  return interpolate(raw, params);
}

/** Persist locale to localStorage. Safe to call outside of React. */
export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  try {
    window.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {}
  // Broadcast change so LocaleProvider instances can sync if needed.
  try {
    window.dispatchEvent(new CustomEvent('localechange', { detail: { locale } }));
  } catch {}
}

/** React context provider. Place near the top of the tree. */
export function LocaleProvider({ children, initialLocale }) {
  const [locale, setLocaleState] = useState(() => initialLocale || detectInitialLocale());

  const updateLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    setLocaleState(next);
    try {
      window.localStorage?.setItem(STORAGE_KEY, next);
    } catch {}
    try {
      document.documentElement.setAttribute('lang', next);
    } catch {}
  }, []);

  // Keep tab sync — other tabs / setLocale() calls.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && SUPPORTED_LOCALES.includes(e.newValue)) {
        setLocaleState(e.newValue);
      }
    };
    const onCustom = (e) => {
      const next = e?.detail?.locale;
      if (next && SUPPORTED_LOCALES.includes(next)) setLocaleState(next);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('localechange', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('localechange', onCustom);
    };
  }, []);

  // Apply lang attribute on mount + when locale changes.
  useEffect(() => {
    try {
      document.documentElement.setAttribute('lang', locale);
    } catch {}
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale: updateLocale,
      t: (key, params) => t(key, locale, params),
    }),
    [locale, updateLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** Main hook used by components. */
export function useLocale() {
  return useContext(LocaleContext);
}

/** Convenience hook returning just the translator bound to current locale. */
export function useT() {
  const { t: translate } = useContext(LocaleContext);
  return translate;
}

export { translations };
